import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';

import ButtonDay from './ButtonDay';
import TimePickerContainer from './TimePickerContainer';
import PlaceContainer from './PlaceContainer';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  section: {
    color: 'white',
    padding: '0 0px',
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  days: {
    color: 'white',
    padding: '0 0px',
    display: 'flex',
    justifyContent: 'center',
  },
  places: {
    display: 'flex',
    flexWrap: 'wrap',
    color: 'black',
  },
  daysplaces: {
    display: 'flex',
    flexDirection: 'column',
  },
  timerange: {
    color: 'white',
    padding: '0 0px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  TimePickerContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttonAdd: {
    marginTop: 'auto!important',
    textTransform: 'none',
    color: '#3f51b5',
  },
  buttonDelete: {
    display: 'inline!important',
    width: '20%',
    marginTop: 'auto!important',
    maxWidth: '10px',
    marginLeft: '0!important',
    padding: '0 0 !important',
  },
}));

export const defaultTimeRange = [
  moment().set('minute', 0).set('hour', 8).add(1, 'day').toDate(),
  moment().set('minute', 0).set('hour', 18).add(1, 'day').toDate(),
];

interface Day {
  id: string;
  day: string;
  selected: boolean;
  identifier: string;
  __typename?: string;
}

interface TimeContainerProps {
  updateWeekDays: (days: any) => void;
  updateTimeFrames: (timeFrame: any, index: string) => void;
  availableDays: Day[];
  indexTimeContainer: string;
  deleteTimeContainer: (e: any, index: string) => void;
  showPlace: boolean;
  initData?: [any, any][];
  inputPlace?: string;
}

const TimeContainer = ({
  updateWeekDays,
  updateTimeFrames,
  availableDays,
  indexTimeContainer,
  deleteTimeContainer,
  showPlace,
  initData,
  inputPlace,
}: TimeContainerProps) => {
  const { classes } = useStyles();

  const [hours, setHours] = useState<[any, any][]>(initData !== undefined ? initData : []);
  const [weekdays, setWeekDays] = useState<Day[]>(availableDays);

  const [place, setPlace] = React.useState<string>(
    inputPlace !== undefined ? inputPlace : '',
  );
  const renderCount = useRef(0);

  const addTimeRange = () => {
    setHours([...hours, [null, null]]);
  };

  const selectDays = (e: React.MouseEvent<HTMLButtonElement>) => {
    const dayId = (e.currentTarget as HTMLButtonElement).dataset.id;
    const selectedDays = [...weekdays].map((day) =>
      day.id === dayId ? { ...day, selected: !day.selected } : day,
    );
    setWeekDays(selectedDays);
  };

  const selectHours = (startTime: any, endTime: any, indexTimer: number) => {
    const newHours: [any, any] = [startTime, endTime];

    if (indexTimer + 1 > hours.length) {
      setHours([...hours, newHours]);
    } else {
      const selectedTimes = [...hours].map((timeRange, ind) => {
        return indexTimer === ind ? newHours : timeRange;
      });

      setHours(selectedTimes);
    }
  };

  const updatePlace = (place: string | null) => {
    setPlace(place || '');
  };

  const deleteTimePickerContainer = (e: any, index: string) => {
    const tempHours = [...hours];

    deleteTimeContainer(e, index);
    tempHours.pop();
    setHours(tempHours);

    if (tempHours.length === 0) {
      deleteTimeContainer(e, index);
    }
  };

  useEffect(() => {
    if (renderCount.current < 2) {
      renderCount.current++;
      return;
    }

    const cleanedHours = [...hours].filter(
      (timeRange) => !timeRange.includes(null),
    );

    const newTimeFrame = [weekdays, cleanedHours, place, indexTimeContainer];

    if (
      hours.length > 0 &&
      ([] as Day[]).concat(...(weekdays as any)).filter((day: Day) => day.selected).length
    ) {
      updateTimeFrames(newTimeFrame, indexTimeContainer);
    }
  }, [weekdays, hours, place]);

  return (
    <div className={classes.container}>
      <div className={classes.section}>
        {weekdays && (
          <>
            <div className={classes.daysplaces}>
              <div className={classes.days}>
                {weekdays.map((day) => {
                  return (
                    <div className="day" key={day.id}>
                      <ButtonDay
                        dayId={day.id}
                        text={day.day}
                        selectDays={selectDays}
                        selected={day.selected}
                      />
                    </div>
                  );
                })}
              </div>
              <div className={classes.places}>
                <PlaceContainer
                  updatePlace={updatePlace}
                  showPlace={showPlace}
                  place={place}
                />
              </div>
            </div>
            <div className={classes.TimePickerContainer}>
              <div className={classes.timerange}>
                {hours.length > 0 &&
                  hours.map((timeRange, index) => {
                    return (
                      <TimePickerContainer
                        key={index}
                        selectHours={selectHours}
                        indexTimer={index}
                        timeRange={timeRange}
                      />
                    );
                  })}
              </div>
              <Button
                className={classes.buttonAdd}
                color="primary"
                onClick={addTimeRange}
              >
                Ajouter des horaires
              </Button>
            </div>
          </>
        )}
      </div>
      <Button
        aria-label="delete"
        className={classes.buttonDelete}
        onClick={(e) => {
          deleteTimePickerContainer(e, indexTimeContainer);
        }}
      >
        <DeleteIcon fontSize="small" />
      </Button>
    </div>
  );
};

export default TimeContainer;
